/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import PencilIcon from '@atlaskit/icon-lab/core/pencil';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { Paper } from '@atlassian/conversation-assistant-ui-components/ui/Paper';
import { CopyResponse } from '@atlassian/rovo-platform-ui-components/message-footer-buttons/copy-response/CopyResponse';

import messages from './messages';

const styles = cssMap({
	wrapper: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		paddingTop: token('space.150'),
	},
	wrapperWithButtonOverflow: {
		paddingBottom: token('space.100'),
	},
	contentWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		overflow: 'visible',
		width: '100%',
	},
	paper: {
		display: 'inline-block',
		wordBreak: 'break-word',
		overflow: 'visible',
		minWidth: '0px',
		whiteSpace: 'nowrap',
		maxWidth: '100%',
	},
	actionButtons: {
		opacity: 0,
		transition: 'opacity 0.2s ease',
		height: '0px',
		overflow: 'visible',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
	copyIconLeft: {
		marginRight: token('space.050', '4px'),
		opacity: 0,
		transition: 'opacity 0.2s ease',
	},
});

export type ContentContainerProps = {
	children: ReactNode;
	leftElement?: ReactNode;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	onContentCopy?: () => void;
	onCreatePrompt?: () => void;
	onEditClick?: () => void;
	shareSlot?: ReactNode;
};

export const ContentContainer = ({
	children,
	onMouseEnter,
	onMouseLeave,
	onContentCopy,
	onCreatePrompt,
	onEditClick,
	shareSlot,
}: ContentContainerProps): JSX.Element => {
	const [isHovered, setIsHovered] = useState(false);
	const { formatMessage } = useIntl();

	const hasOverflowActions = !!(onCreatePrompt || onEditClick);

	return (
		<Box
			xcss={cx(styles.wrapper, hasOverflowActions && styles.wrapperWithButtonOverflow)}
			onMouseEnter={() => {
				setIsHovered(true);
				onMouseEnter?.();
			}}
			onMouseLeave={() => {
				setIsHovered(false);
				onMouseLeave?.();
			}}
		>
			<Box xcss={styles.contentWrapper}>
				{onContentCopy && !onCreatePrompt && !onEditClick && (
					<Box xcss={styles.copyIconLeft} style={{ opacity: isHovered ? 1 : 0 }}>
						<Inline space="space.050" alignBlock="center">
							<CopyResponse
								onClick={onContentCopy}
								{...{
									appearance: 'subtle',
									spacing: 'default',
									shape: 'default',
								}}
							/>
							{shareSlot}
						</Inline>
					</Box>
				)}
				<Box xcss={styles.paper}>
					<Paper
						variant="accentV2"
						verticalSpace={false}
						pinchCorner="bottomRight"
						isHovered={isHovered}
					>
						{children}
					</Paper>
				</Box>
			</Box>
			{hasOverflowActions && (
				<Box xcss={styles.actionButtons} style={{ opacity: isHovered ? 1 : 0 }}>
					<Inline space="space.050">
						{onEditClick && (
							<IconButton
								isTooltipDisabled={false}
								type="button"
								onClick={onEditClick}
								icon={(iconProps) => (
									<PencilIcon {...iconProps} color={token('color.icon.subtle')} />
								)}
								appearance="subtle"
								spacing="default"
								label={formatMessage(messages.rewriteTooltip)}
								shape="default"
							/>
						)}
						{onContentCopy && (
							<CopyResponse
								onClick={onContentCopy}
								{...{
									appearance: 'subtle',
									spacing: 'default',
									shape: 'default',
								}}
							/>
						)}
						{shareSlot}
						{onCreatePrompt && (
							<IconButton
								isTooltipDisabled={false}
								type="button"
								onClick={onCreatePrompt}
								icon={(iconProps) => (
									<PlaylistAddIcon {...iconProps} color={token('color.icon.subtle')} />
								)}
								appearance="subtle"
								spacing="default"
								label={formatMessage(messages.createPromptLabel)}
								shape="default"
							/>
						)}
					</Inline>
				</Box>
			)}
		</Box>
	);
};
